
import { Clock, Play, Pause } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useGpsTracking } from "@/hooks/useGpsTracking";
import GpsPositionDisplay from "./GpsPositionDisplay";
import { useToast } from "@/hooks/use-toast";
import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { ManualTimeEntry } from "./ManualTimeEntry";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const GpsTracker = () => {
  const { tracking, position, nearestLocation, startTracking, stopTracking } = useGpsTracking();
  const { toast } = useToast();
  const [tasks, setTasks] = useState([]);
  const [userId, setUserId] = useState<string | null>(null);
  const [selectedTask, setSelectedTask] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          setUserId(session.user.id);
          
          // Tjek om brugeren er admin
          const { data: isAdmin } = await supabase.rpc('has_role', {
            user_id: session.user.id,
            role: 'admin'
          });

          // Hent opgaver baseret på brugerrolle
          const query = supabase
            .from("tasks")
            .select("*, projects(title)")
            .eq(isAdmin ? 'status' : 'assigned_to', isAdmin ? 'pending' : session.user.email)
            .order('created_at', { ascending: false });

          const { data: tasksData, error } = await query;

          if (error) throw error;
          setTasks(tasksData || []);
        }
      } catch (error) {
        console.error("Fejl ved indlæsning af data:", error);
        toast({
          title: "Fejl ved indlæsning",
          description: "Kunne ikke hente dine opgaver",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  const handleStartTracking = async () => {
    if (!selectedTask) {
      toast({
        title: "Vælg opgave",
        description: "Du skal vælge en opgave før du kan starte tidsregistrering",
        variant: "destructive",
      });
      return;
    }

    try {
      await startTracking(selectedTask);
      toast({
        title: "Tidsregistrering startet",
        description: "Din arbejdstid bliver nu registreret",
      });
    } catch (error) {
      console.error("Fejl ved start af tracking:", error);
      toast({
        title: "Kunne ikke starte GPS tracking",
        description: "Tjek om du har givet tilladelse til at bruge din position",
        variant: "destructive",
      });
    }
  };

  const handleStopTracking = async () => {
    try {
      await stopTracking();
      setSelectedTask(""); // Reset task selection when stopping
      toast({
        title: "Tidsregistrering afsluttet",
        description: "Din arbejdstid er blevet gemt",
      });
    } catch (error) {
      console.error("Fejl ved stop af tracking:", error);
      toast({
        title: "Fejl ved stop af tracking",
        description: "Prøv igen eller kontakt support hvis problemet fortsætter",
        variant: "destructive",
      });
    }
  };

  const getTaskLabel = (task: any) => {
    let label = task.title;
    if (task.projects?.title) {
      label += ` (${task.projects.title})`;
    }
    return label;
  };

  return (
    <Card className="p-6 space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <Clock className="w-5 h-5" />
          Tidsregistrering
        </h2>
      </div>

      <Tabs defaultValue="gps" className="w-full">
        <TabsList className="w-full">
          <TabsTrigger value="gps" className="w-full">GPS Tracking</TabsTrigger>
          <TabsTrigger value="manual" className="w-full">Manuel registrering</TabsTrigger>
        </TabsList>

        <TabsContent value="gps" className="space-y-4">
          {!tracking && (
            <div>
              <Select 
                value={selectedTask} 
                onValueChange={setSelectedTask}
                disabled={isLoading}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={isLoading ? "Indlæser opgaver..." : "Vælg opgave"} />
                </SelectTrigger>
                <SelectContent>
                  {tasks.map((task: any) => (
                    <SelectItem key={task.id} value={task.id}>
                      {getTaskLabel(task)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <Button
            onClick={tracking ? handleStopTracking : handleStartTracking}
            variant={tracking ? "destructive" : "default"}
            size="lg"
            className="w-full"
            disabled={!tracking && !selectedTask}
          >
            {tracking ? (
              <>
                <Pause className="w-4 h-4 mr-2" />
                Stop Arbejdstid
              </>
            ) : (
              <>
                <Play className="w-4 h-4 mr-2" />
                Start Arbejdstid
              </>
            )}
          </Button>

          {position && <GpsPositionDisplay position={position} nearestLocation={nearestLocation} />}

          {tracking && (
            <div className="text-sm text-green-600 font-medium flex items-center gap-2">
              <div className="w-2 h-2 bg-green-600 rounded-full animate-pulse" />
              Tidsregistrering aktiv
              {nearestLocation && ` ved ${nearestLocation.title}`}
            </div>
          )}
        </TabsContent>

        <TabsContent value="manual">
          {userId && (
            <ManualTimeEntry 
              userId={userId} 
              tasks={tasks} 
              onTimeRegistered={() => {
                // Handle time registration update if needed
              }} 
            />
          )}
        </TabsContent>
      </Tabs>
    </Card>
  );
};

export default GpsTracker;
